import { AppLayout, ContentLayout } from '@amzn/awsui-components-core';
import Spinner from '@amzn/awsui-components-core/spinner';
import React, { useEffect } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { LoadingStatus } from 'src/components/context/AppContextModels';
import { useAppContext } from 'src/components/context/AppContextProvider';
import { useAskQuick } from 'src/components/ask-quick/AskQuickContext';
import { EmbedApp } from 'src/components/das-finsuite/embedded-apps/EmbedApp';
import { EmptyState } from 'src/components/generic-components/empty-state';

export const EmbedAppPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const resetKey = searchParams.get('t') || '';
  const { listOfApplications, contextLoadingStatus } = useAppContext();
  const { setActiveAgentUrl } = useAskQuick();

  // Resolve the application early so the effect can reference it
  const application = contextLoadingStatus === LoadingStatus.Completed ? listOfApplications.find((app) => app.id === id) : undefined;
  const agentUrl = application?.qChatConfig?.isEnabled ? application.qChatConfig.agentUrl : null;

  // When the embedded app has a qChatConfig with an agent URL, surface it
  // in the QuickSuite panel. Reset back to the default agent on unmount.
  useEffect(() => {
    if (agentUrl) {
      setActiveAgentUrl(agentUrl);
    }
    return () => setActiveAgentUrl(null);
  }, [agentUrl, setActiveAgentUrl]);

  if (contextLoadingStatus !== LoadingStatus.Completed) {
    return <Spinner size="large" />;
  }

  if (!application) {
    return <EmptyState title="Application Not Found" description="The application you are looking for does not exist." verticalCenter={true} />;
  }

  if (!application.embeddingEnabled) {
    return <Navigate to="/all-apps" replace />;
  }

  return (
    <AppLayout
      contentType="dashboard"
      disableContentPaddings
      navigationHide
      toolsHide
      maxContentWidth={Number.MAX_VALUE}
      content={
        <ContentLayout disableOverlap>
          <EmbedApp key={`${id}-${resetKey}`} application={application} />
        </ContentLayout>
      }
    />
  );
};
